// PDF generation using jsPDF (no autotable needed — we render manually
// to keep the lab/zine aesthetic and stay dependency-light).
import { jsPDF } from "jspdf";

const PAGE_PAD = 48;
const LINE_GAP = 6;
const COLOR_INK = [10, 10, 20];
const COLOR_LIME = [80, 130, 0];
const COLOR_CYAN = [0, 120, 145];
const COLOR_CORAL = [200, 35, 70];
const COLOR_MUTED = [110, 110, 130];

function safeName(s) {
    return (
        (s || "untitled")
            .replace(/[^a-z0-9_-]+/gi, "_")
            .slice(0, 60)
            .replace(/^_+|_+$/g, "") || "untitled"
    );
}

function newDoc() {
    return new jsPDF({ unit: "pt", format: "letter" });
}

// Wrap-and-write helper. Returns new y.
function writeWrapped(doc, text, x, y, opts = {}) {
    const {
        font = "helvetica",
        style = "normal",
        size = 11,
        color = COLOR_INK,
        lineHeight = 1.35,
        maxWidth,
    } = opts;
    doc.setFont(font, style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    const w = maxWidth ?? doc.internal.pageSize.getWidth() - PAGE_PAD * 2;
    const lines = doc.splitTextToSize(text || "", w);
    const lh = size * lineHeight;
    lines.forEach((line) => {
        if (y > doc.internal.pageSize.getHeight() - PAGE_PAD) {
            doc.addPage();
            y = PAGE_PAD;
        }
        doc.text(line, x, y);
        y += lh;
    });
    return y;
}

function drawHr(doc, y, color = COLOR_MUTED) {
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(0.5);
    doc.line(PAGE_PAD, y, doc.internal.pageSize.getWidth() - PAGE_PAD, y);
    return y + 10;
}

function sectionHeader(doc, y, label, color = COLOR_LIME) {
    if (y > doc.internal.pageSize.getHeight() - 120) {
        doc.addPage();
        y = PAGE_PAD;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(color[0], color[1], color[2]);
    doc.text(label.toUpperCase(), PAGE_PAD, y);
    return drawHr(doc, y + 6, color);
}

function brandHeader(doc) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(COLOR_MUTED[0], COLOR_MUTED[1], COLOR_MUTED[2]);
    doc.text("FLOW.LAB  //  URBAN AQUAPONICS", PAGE_PAD, 24);
    doc.setDrawColor(COLOR_LIME[0], COLOR_LIME[1], COLOR_LIME[2]);
    doc.setLineWidth(1.2);
    doc.line(PAGE_PAD, 30, doc.internal.pageSize.getWidth() - PAGE_PAD, 30);
}

function brandFooter(doc, label = "") {
    const pageCount = doc.internal.getNumberOfPages();
    for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        const h = doc.internal.pageSize.getHeight();
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor(COLOR_MUTED[0], COLOR_MUTED[1], COLOR_MUTED[2]);
        doc.text(`${label}  ·  page ${p} of ${pageCount}`, PAGE_PAD, h - 18);
        doc.setTextColor(COLOR_LIME[0], COLOR_LIME[1], COLOR_LIME[2]);
        doc.text(
            "flow.lab",
            doc.internal.pageSize.getWidth() - PAGE_PAD,
            h - 18,
            { align: "right" },
        );
    }
}

// ---------- Lesson Plan ----------
// Parses simple markdown (## headings, **bold**, - bullets, plain paragraphs)
export function exportLessonPlanPdf(plan) {
    const doc = newDoc();
    brandHeader(doc);
    let y = 60;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(20);
    doc.setTextColor(COLOR_INK[0], COLOR_INK[1], COLOR_INK[2]);
    y = writeWrapped(doc, plan.topic || "Lesson Plan", PAGE_PAD, y, {
        size: 20,
        style: "bold",
    });

    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(COLOR_MUTED[0], COLOR_MUTED[1], COLOR_MUTED[2]);
    const meta = `Grade ${plan.grade_level || "—"}  ·  ${plan.duration_min || "—"} min`;
    y = writeWrapped(doc, meta, PAGE_PAD, y + 4, { size: 10, color: COLOR_MUTED });
    y = drawHr(doc, y + 4);

    const md = plan.plan_markdown || "";
    const lines = md.split("\n");
    for (const raw of lines) {
        const line = raw.replace(/\s+$/, "");
        if (!line) {
            y += 6;
            continue;
        }
        if (line.startsWith("# ")) {
            // already shown as topic; skip
            continue;
        }
        if (line.startsWith("## ")) {
            y = sectionHeader(doc, y + 6, line.slice(3));
            continue;
        }
        if (line.startsWith("### ")) {
            y = writeWrapped(doc, line.slice(4), PAGE_PAD, y + 4, {
                size: 12,
                style: "bold",
                color: COLOR_CYAN,
            });
            continue;
        }
        if (/^[-*]\s+/.test(line)) {
            const txt = line.replace(/^[-*]\s+/, "•  ");
            y = writeWrapped(doc, txt, PAGE_PAD + 8, y, {
                size: 11,
                maxWidth: doc.internal.pageSize.getWidth() - PAGE_PAD * 2 - 8,
            });
            continue;
        }
        // strip **bold** markers — render plainly
        const cleaned = line.replace(/\*\*(.+?)\*\*/g, "$1");
        y = writeWrapped(doc, cleaned, PAGE_PAD, y, { size: 11 });
        y += LINE_GAP / 2;
    }

    brandFooter(doc, plan.topic || "Lesson Plan");
    doc.save(`flow_lesson_${safeName(plan.topic)}.pdf`);
}

// ---------- Week Handout ----------
export function exportWeekHandoutPdf(week, lang = "en") {
    const labels =
        lang === "es"
            ? {
                  module: "MÓDULO",
                  of: "DE",
                  objectives: "OBJETIVOS",
                  activities: "ACTIVIDADES",
                  materials: "MATERIALES",
                  vocab: "VOCABULARIO",
                  discussion: "PREGUNTAS",
                  quiz: "QUIZ",
              }
            : {
                  module: "MODULE",
                  of: "OF",
                  objectives: "LEARNING OBJECTIVES",
                  activities: "HANDS-ON ACTIVITIES",
                  materials: "MATERIALS",
                  vocab: "VOCABULARY",
                  discussion: "DISCUSSION PROMPTS",
                  quiz: "QUIZ",
              };

    const doc = newDoc();
    brandHeader(doc);
    let y = 60;

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(COLOR_CORAL[0], COLOR_CORAL[1], COLOR_CORAL[2]);
    doc.text(
        `${labels.module} 0${week.week} ${labels.of} 06   ·   ${week.duration}`,
        PAGE_PAD,
        y,
    );
    y += 18;

    y = writeWrapped(doc, week.title, PAGE_PAD, y, {
        size: 22,
        style: "bold",
    });
    y = writeWrapped(doc, week.tagline, PAGE_PAD, y + 2, {
        size: 11,
        style: "italic",
        color: COLOR_MUTED,
    });
    y = drawHr(doc, y + 6);

    // Objectives
    y = sectionHeader(doc, y, labels.objectives, COLOR_LIME);
    (week.objectives || []).forEach((o, i) => {
        y = writeWrapped(doc, `${i + 1}.  ${o}`, PAGE_PAD + 4, y, { size: 11 });
    });

    // Activities
    y = sectionHeader(doc, y + 8, labels.activities, COLOR_CYAN);
    (week.activities || []).forEach((a) => {
        y = writeWrapped(doc, `•  ${a}`, PAGE_PAD + 4, y, { size: 11 });
    });

    // Materials
    y = sectionHeader(doc, y + 8, labels.materials, COLOR_CORAL);
    y = writeWrapped(
        doc,
        (week.materials || []).map((m) => `• ${m}`).join("    "),
        PAGE_PAD,
        y,
        { size: 11 },
    );

    // Vocab
    y = sectionHeader(doc, y + 8, labels.vocab, COLOR_LIME);
    (week.vocab || []).forEach((v) => {
        y = writeWrapped(doc, v.term, PAGE_PAD, y, {
            size: 12,
            style: "bold",
            color: COLOR_INK,
        });
        y = writeWrapped(doc, v.def, PAGE_PAD + 6, y, {
            size: 10,
            color: COLOR_MUTED,
        });
        y += 4;
    });

    // Discussion
    y = sectionHeader(doc, y + 4, labels.discussion, COLOR_CYAN);
    (week.discussion || []).forEach((d) => {
        y = writeWrapped(doc, `→  ${d}`, PAGE_PAD + 4, y, { size: 11 });
        y += 2;
    });

    // Quiz (questions only — blank for hand-write answers)
    y = sectionHeader(doc, y + 8, labels.quiz, COLOR_CORAL);
    (week.quiz || []).forEach((q, qi) => {
        y = writeWrapped(doc, `Q${qi + 1}.  ${q.q}`, PAGE_PAD, y, {
            size: 11,
            style: "bold",
        });
        q.options.forEach((opt, oi) => {
            const letter = String.fromCharCode(65 + oi);
            y = writeWrapped(
                doc,
                `  ☐  ${letter}.  ${opt}`,
                PAGE_PAD + 6,
                y,
                { size: 10 },
            );
        });
        y += 6;
    });

    brandFooter(doc, `Week ${week.week} — ${week.title}`);
    doc.save(`flow_week${week.week}_${safeName(week.title)}.pdf`);
}
